import { useCurrency } from '@bz/common';
import { debounce } from '@solid-primitives/scheduled';

import { useBonusConfig } from '../../shared/hooks';
import { BetSlipType } from '../../../types';
import { useMaxStakeAction, useBetSlipStore } from '../../../useBetSlip';

interface StakeTipProps {
  amount: number;
  code?: string;
  matchId?: string;
  latestOdds?: number;
  isShowOddsLimit: Accessor<boolean>;
  onVisibleChange?: (visible: boolean) => void;
}

export const BetStakeInputTip = (props: StakeTipProps) => {
  const { latestCurrency, isCash, addVipBalance } = useCurrency();
  const fetchMaxStake = useMaxStakeAction();
  const [store, dispatch] = useBetSlipStore();
  const { minBet, maxBet, minBetOdds } = useBonusConfig();
  // 1. 内部维护局部状态，不干扰外部
  const [localMaxStake, setLocalMaxStake] = createSignal<number | undefined>(undefined);

  const getBalance = createMemo(() => addVipBalance());
  // 2. 内部集成请求逻辑
  const debouncedFetch = debounce(async (amount: number) => {
    try {
      const res = await fetchMaxStake({
        amount,
        ...(props.code ? { code: props.code } : {}),
        ...(props.matchId ? { matchId: props.matchId } : {}),
      });
      if (res?.bets) setLocalMaxStake(res.bets[0].amount);
    } catch (e) {
      console.warn('Max stake fetch failed', e);
    }
  }, 600);

  // 3. 监听金额变化自动触发请求 (Effect)
  createComputed(() => {
    if (account.isLogin && getBalance() > 0) {
      if (props.amount > 0) {
        debouncedFetch(props.amount);
      } else {
        setLocalMaxStake(undefined);
        debouncedFetch.clear();
      }
    }
  });

  onCleanup(() => debouncedFetch.clear());

  // 4. 判断逻辑 (Min, Max, Unavailable)
  const currentCurrencyInfo = createMemo(() => currencyConfig[latestCurrency()]);
  const bonusMax = createMemo(() => {
    return localMaxStake() && Math.min(localMaxStake()!, +maxBet());
  });

  const stakeStatus = createMemo(() => {
    const amount = props.amount;
    let max = localMaxStake();
    let min = currentCurrencyInfo()?.minBet;
    if (!isCash()) {
      min = +minBet();
      max = bonusMax();
    }
    if (amount <= 0) return null;
    if (props.isShowOddsLimit() && !isCash()) return 'odds';
    if (max !== undefined && min !== undefined && max < min) return 'unavailable';
    if (min !== undefined && amount < min) return 'min';
    if (max !== undefined && amount > max) return 'max';
    return null;
  });

  // 依据币种来确定小数位
  const truncateDecimal = (value: number, decimals = 0) => {
    const factor = Math.pow(10, decimals);
    return Math.trunc(value * factor) / factor;
  };

  // const isShowBonusTips =
  // 5. UI 数据配置
  const config = createMemo(() => {
    const status = stakeStatus();

    if (!status) return null;

    const showUnit = currentCurrencyInfo()?.showUnit ?? 2;
    const maxVal = truncateDecimal(Number(localMaxStake()), showUnit);

    const map = {
      max: {
        label: `${t('Max stake amount is')} ${maxVal}`,
        fixValue: maxVal,
      },
      min: {
        label: `${t('Minimum stake must be above')} ${currentCurrencyInfo()?.minBet}`,
        fixValue: currentCurrencyInfo()?.minBet,
      },
      unavailable: { label: t('Market unavailable due to limits'), fixValue: null },
      odds: null,
    };

    const bonusMaxVal = truncateDecimal(Number(bonusMax()), showUnit);
    const bonusMinVal = truncateDecimal(Number(minBet()), showUnit);
    const bonusMap = {
      max: {
        label: `${t('Max stake amount is')} ${bonusMaxVal}`,
        fixValue: bonusMaxVal,
      },
      min: {
        label: `${t('Minimum stake must be above')} ${bonusMinVal}`,
        fixValue: bonusMinVal,
      },
      unavailable: { label: t('Market unavailable due to limits'), fixValue: null },
      odds: { label: `${t('Minimum odds for Bonus is')} ${minBetOdds()}`, fixValue: null },
    };
    if (isCash()) {
      return map[status];
    }
    return bonusMap[status];
  });

  const handleInput = (value: any) => {
    const amount = value === '' ? 0 : Number(value);
    const actions = {
      [BetSlipType.Single]: () => dispatch({ type: 'UPDATE_ODD_AMOUNT', code: props.code!, amount }),
      [BetSlipType.Combo]: () => dispatch({ type: 'SET_COMBO_AMOUNT', amount }),
      [BetSlipType.System]: () => dispatch({ type: 'SET_COMBO_AMOUNT', amount }),
    };

    actions[store.betType]?.();
  };

  createEffect(() => {
    const status = stakeStatus();

    dispatch({
      type: 'SET_BET_INPUT_LIMIT_TIP',
      isLimit: status !== null,
    });
  });

  createEffect(() => {
    props.onVisibleChange?.(Boolean(config()));
  });

  onCleanup(() => {
    props.onVisibleChange?.(false);
  });

  return (
    <Show when={config()}>
      {(item) => (
        <div
          class="text-xs cursor-pointer flex bg-alpha-function-orange-a10 py-2 rounded-lg mt-2 mb-1 items-center"
          onClick={() => item().fixValue && handleInput(item().fixValue)}
        >
          <IconCheckWarning class="size-6 text-function-alert mx-2" />
          <div class="flex flex-col font-semibold">
            <span class="text-neutral-main1 cursor-pointer">{item().label}</span>
            <Show when={item().fixValue}>
              <span class="text-neutral-main3 cursor-pointer">{t('Tap to update stake amount')}</span>
            </Show>
          </div>
        </div>
      )}
    </Show>
  );
};
